import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/common/Accordion';
import { Pagination } from '@/components/common/Pagination';
import {
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from '@/components/common/Table';
import type { ReactNode } from 'react';
import { Fragment, useEffect, useRef } from 'react';

interface PaginationMeta {
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

interface DashboardTableAccordionProps<T> {
  // Accordion props
  value: string;
  title: string;
  accordionOpenValue?: string;
  onOpen?: () => void;

  // Optional summary section (like Orders stats)
  summarySection?: ReactNode;

  // Optional filters component
  filtersComponent?: ReactNode;

  // Data and loading state
  data: Array<T>;
  isLoading?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;

  // Pagination
  paginationMeta?: PaginationMeta;
  onPageChange?: (page: number) => void;

  // Table rendering - either use custom table or render props
  customTable?: ReactNode;
  renderHeader?: () => ReactNode;
  renderRow?: (item: T, index: number) => ReactNode;

  // If true, renderRow should return TableRow(s) instead of TableCell(s)
  multiRow?: boolean;

  // Optional footer actions (like "Add New Record" button)
  footerActions?: ReactNode;

  // Optional custom wrapper for table (like Card)
  tableWrapper?: 'none' | 'card';

  // Optional className overrides
  className?: string;
  tableClassName?: string;
}

export function DashboardTableAccordion<T extends { id: number | string }>({
  value,
  title,
  accordionOpenValue,
  onOpen,
  summarySection,
  filtersComponent,
  data,
  isLoading = false,
  emptyMessage = '',
  loadingMessage = 'Loading...',
  paginationMeta,
  onPageChange,
  customTable,
  renderHeader,
  renderRow,
  multiRow = false,
  footerActions,
  tableWrapper = 'none',
  className,
  tableClassName,
}: DashboardTableAccordionProps<T>) {
  const wasOpenRef = useRef(accordionOpenValue === value);
  useEffect(() => {
    const isOpen = accordionOpenValue === value;
    if (isOpen && !wasOpenRef.current && onOpen) {
      onOpen();
    }
    wasOpenRef.current = isOpen;
  }, [accordionOpenValue, value, onOpen]);

  const tableContent = (
    <>
      {summarySection && <div className="mb-6">{summarySection}</div>}

      {filtersComponent && <div className="mb-6">{filtersComponent}</div>}

      <div className="w-full overflow-x-auto">
        {isLoading ? (
          <p className="text-slate-500">{loadingMessage}</p>
        ) : data.length === 0 ? (
          <p className="text-slate-500">{emptyMessage}</p>
        ) : customTable ? (
          <>
            {customTable}

            {paginationMeta && onPageChange && (
              <Pagination
                currentPage={paginationMeta.current_page}
                lastPage={paginationMeta.last_page}
                total={paginationMeta.total}
                perPage={paginationMeta.per_page}
                onPageChange={onPageChange}
                isLoading={isLoading}
              />
            )}
          </>
        ) : renderHeader && renderRow ? (
          <>
            <Table className={tableClassName}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {renderHeader()}
                </TableRow>
              </TableHeader>
              <TableBody>
                {multiRow
                  ? data.map((item, index) => (
                      <Fragment key={item.id}>
                        {renderRow(item, index)}
                      </Fragment>
                    ))
                  : data.map((item, index) => (
                      <TableRow key={item.id}>
                        {renderRow(item, index)}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>

            {paginationMeta && onPageChange && (
              <Pagination
                currentPage={paginationMeta.current_page}
                lastPage={paginationMeta.last_page}
                total={paginationMeta.total}
                perPage={paginationMeta.per_page}
                onPageChange={onPageChange}
                isLoading={isLoading}
              />
            )}
          </>
        ) : null}
      </div>

      {footerActions && <div className="mt-4">{footerActions}</div>}
    </>
  );

  return (
    <AccordionItem
      value={value}
      className={className || 'bg-background rounded-2xl border-none p-5.5'}
    >
      <AccordionTrigger className="text-table-foreground text-xl font-normal hover:no-underline">
        {title}
      </AccordionTrigger>
      <AccordionContent>
        {tableWrapper === 'card' ? (
          <div className="bg-background rounded-2xl">{tableContent}</div>
        ) : (
          <div className="space-y-6">{tableContent}</div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}
